/**
 * Knowledge Graph API Service
 */

import { apiClient as api } from './apiClient';
import { API_BASE_URL } from '../config';

const KG_API_BASE = `${API_BASE_URL}/api/v1/knowledge-graph`;

// Types
export interface GraphTreeNode {
    id: string;
    code: string;
    labels: Record<string, string>;
    description: Record<string, string> | null;
    category: 'grammar' | 'vocabulary' | 'usage';
    cefrLevelMin: string;
    cefrLevelMax: string;
    sortOrder: number;
    icon: string | null;
    children: GraphTreeNode[];
    knowledgePointCount?: number;
    patternCount?: number;
}

export interface UserNodeMastery {
    id: string;
    userId: string;
    nodeId: string;
    masteryScore: number;
    confidence: number;
    encounterCount: number;
    correctCount: number;
    trend: 'improving' | 'stable' | 'declining';
    lastActivityAt: string | null;
    node: {
        id: string;
        code: string;
        labels: Record<string, string>;
        category: string;
        cefrLevelMin: string;
        cefrLevelMax: string;
    };
}

export interface ProficiencySummary {
    overallCefrEstimate: string;
    categoryScores: {
        grammar: number;
        vocabulary: number;
        usage: number;
    };
    radarData: {
        vocabularyMastery: number;
        grammarAccuracy: number;
        sentenceStructure: number;
        listening: number | null;
        speaking: number | null;
        writing: number | null;
    };
    categoryCefrLevels?: {
        grammar: string;
        vocabulary: string;
        usage: string;
    };
    strengths: { code: string; label: string; mastery: number }[];
    weaknesses: { code: string; label: string; mastery: number }[];
    recentProgress: { code: string; label: string; masteryDelta: number }[];
    totalNodesLearned: number;
    totalNodesEstimated: number;
    totalNodes: number;
    /** Per-dimension CEFR levels + trends */
    dimensionDetails?: {
        vocabulary: { cefr: string; score: number; trend: string; dataPoints: number };
        grammar: { cefr: string; score: number; trend: string; dataPoints: number };
        sentence: { cefr: string; score: number; trend: string; dataPoints: number };
        listening: { cefr: string; score: number; trend: string; dataPoints: number };
        speaking: { cefr: string; score: number; trend: string; dataPoints: number };
        writing: { cefr: string; score: number; trend: string; dataPoints: number };
    };
    /** Overall confidence (0-1) */
    confidence?: number;
    /** Total data points */
    dataPoints?: number;
    /** true when all mastery data comes from placement test (no real learning yet) */
    isPlacementOnly: boolean;
}

export interface CourseCoverage {
    courseId: string;
    coveredNodes: {
        nodeId: string;
        code: string;
        labels: Record<string, string>;
        category: string;
        knowledgePointCount: number;
        patternCount: number;
    }[];
    coveragePercentage: number;
    cefrRange: { min: string; max: string };
}

// API calls
export async function fetchGraphTree(): Promise<GraphTreeNode[]> {
    const response = await api.get(`${KG_API_BASE}/tree`);
    return response.data;
}

export async function fetchNodeDetail(nodeId: string): Promise<any> {
    const response = await api.get(`${KG_API_BASE}/nodes/${nodeId}`);
    return response.data;
}

export async function fetchProficiency(): Promise<UserNodeMastery[]> {
    const response = await api.get(`${KG_API_BASE}/proficiency`);
    return response.data;
}

export async function fetchProficiencySummary(lang?: string): Promise<ProficiencySummary> {
    const params = lang ? `?lang=${lang}` : '';
    const response = await api.get(`${KG_API_BASE}/proficiency/summary${params}`);
    return response.data;
}

export async function initializeProficiency(placementResultId?: string): Promise<void> {
    await api.post(`${KG_API_BASE}/proficiency/initialize`, { placementResultId });
}

export async function resetProficiency(placementResultId?: string): Promise<void> {
    await api.post(`${KG_API_BASE}/proficiency/reset`, { placementResultId });
}

export async function fetchCourseCoverage(courseId: string): Promise<CourseCoverage> {
    const response = await api.get(`${KG_API_BASE}/course/${courseId}/coverage`);
    return response.data;
}
